import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import {
  fetchAvailableTimesForDate,
  type DateFlowTimeSlot,
} from '../../../utils/reservation/by-Date/fetchAvailableTimesForDate';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import { isPastTimeSlot } from '../../../utils/reservation/filterPastTimeSlots';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';
import { postAvailableTimesByService } from '../../../utils/reservation/by-service/postAvailableTimesByService';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import { useI18n } from '../../../i18n';

const SelectTimeByDate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStaffId,
    selectedStaffName,
    selectedServices,
    selectedStartTime,
    setSelectedStartTime,
    setSelectedServices,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
    isFreeReservation,
  } = useReserveContext();

  const [slots, setSlots] = useState<DateFlowTimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-date/store');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate('/reserve/by-date/date');
      return;
    }
    if (isEditing && (serviceIds.length === 0 || (!selectedStaffId && !isFreeReservation))) {
      toast.info(t('time.returnAfterStaffServices'));
      navigate('/reserve/by-date/confirm-reservation');
      return;
    }
  }, [storeId, selectedDate, selectedStaffId, isFreeReservation, serviceIds, isEditing, navigate, t]);

  useEffect(() => {
    const load = async () => {
      if (!storeId || !selectedDate) return;
      setLoading(true);
      try {
        if (!isEditing) {
          const data = await fetchAvailableTimesForDate(storeId, selectedDate);
          setSlots(data);
          return;
        }

        if (isFreeReservation) {
          const baseSlots = await fetchAvailableTimesForDate(storeId, selectedDate);
          const filteredSlots = await Promise.all(
            baseSlots.map(async (slot) => {
              if (!slot.available) return slot;
              const options = await fetchStaffOptionsForDate({
                storeId,
                date: selectedDate,
                startTime: slot.time,
                serviceIds,
              });
              return {
                ...slot,
                available: options.free_available,
              };
            }),
          );
          setSlots(filteredSlots);
          return;
        }

        if (!selectedStaffId) {
          setSlots([]);
          return;
        }

        const durations = await fetchStaffServiceDurations(selectedStaffId, serviceIds);
        const durationMap = new Map(
          durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
        );
        const patchedServices = selectedServices.map((service) => {
          const duration = durationMap.get(Number(service.serviceId));
          return duration != null && Number.isFinite(duration) && duration > 0
            ? { ...service, duration_minutes: duration, staffId: selectedStaffId, staffName: selectedStaffName }
            : service;
        });
        const changed = patchedServices.some(
          (service, index) =>
            Number(service.duration_minutes) !== Number(selectedServices[index]?.duration_minutes),
        );
        if (changed) {
          setSelectedServices(patchedServices);
        }

        const durationMinutes = patchedServices.reduce(
          (sum, service) => sum + (Number(service.duration_minutes) || 0),
          0,
        );
        const data = await postAvailableTimesByService({
          store_id: storeId,
          staff_id: selectedStaffId,
          date: selectedDate,
          duration_minutes: durationMinutes,
          service_ids: serviceIds,
        });
        setSlots(data);
      } catch (err) {
        console.error(err);
        toast.error(t('time.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [
    storeId,
    selectedDate,
    selectedStaffId,
    selectedStaffName,
    selectedServices,
    serviceIds,
    isFreeReservation,
    isEditing,
    setSelectedServices,
    t,
  ]);

  useEffect(() => {
    if (!selectedStartTime || !selectedDate) return;

    const selectedSlot = slots.find((slot) => slot.time === selectedStartTime);
    if (!selectedSlot || !selectedSlot.available || isPastTimeSlot(selectedDate, selectedStartTime)) {
      setSelectedStartTime(null);
    }
  }, [slots, selectedDate, selectedStartTime, setSelectedStartTime]);

  const handleSelectTime = (time: string, available: boolean) => {
    if (!available || (selectedDate && isPastTimeSlot(selectedDate, time))) return;
    setSelectedStartTime(time);
    if (!isEditing) {
      setSelectedServices([]);
      setSelectedStaffId(null);
      setSelectedStaffName(null);
      setIsFreeReservation(false);
    }
  };

  const handleNext = () => {
    if (!selectedStartTime) {
      toast.warning(t('time.selectWarning'));
      return;
    }
    navigate(isEditing ? '/reserve/by-date/confirm-reservation' : '/reserve/by-date/staff');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('time.chooseTitle')}
        onBack={() =>
          navigate(isEditing ? '/reserve/by-date/confirm-reservation' : '/reserve/by-date/date')
        }
        step={3}
        totalSteps={5}
      />

      {loading ? (
        <p className="mt-2 text-center text-sm text-slate-400">{t('time.loading')}</p>
      ) : slots.length === 0 ? (
        isEditing ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">{t('time.cannotChange')}</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  {t('time.noAlternative')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-center text-sm text-slate-400">
            {t('time.noAvailable')}
          </p>
        )
      ) : (
        <div className="mt-2 rounded-[28px] border border-[#d7efee] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] p-4 shadow-[0_14px_30px_rgba(10,186,181,0.08)]">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5a9d99]">
            {t('time.availableSlots')}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {slots.map((slot) => {
              const isSelected = selectedStartTime === slot.time;
              const isPast = selectedDate ? isPastTimeSlot(selectedDate, slot.time) : false;
              const isDisabled = !slot.available || isPast;

              return (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => handleSelectTime(slot.time, slot.available)}
                  disabled={isDisabled}
                  className={[
                    'min-h-[52px] rounded-2xl border text-sm font-semibold transition',
                    isDisabled
                      ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                      : isSelected
                      ? 'border-[#0ABAB5] bg-[#0ABAB5] text-white shadow-[0_12px_22px_rgba(10,186,181,0.24)]'
                      : 'border-[#d5ecea] bg-white text-slate-700 hover:border-[#9fdeda] hover:bg-[#f4fcfb]',
                  ].join(' ')}
                >
                  {slot.time}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ReserveStepFooter
        summary={selectedStartTime ? t('time.selectedSummary', { time: selectedStartTime }) : t('time.selectSummary')}
        primaryLabel={t('footer.next')}
        onPrimary={handleNext}
        primaryDisabled={!selectedStartTime}
      />
    </div>
  );
};

export default SelectTimeByDate;
